import { useState, useEffect } from "react";
import { useAbortedEffect, useHookForm } from "../../../../components/hooks";
import { useDispatch } from "react-redux";
import { setProgress, setToast } from "../../../../redux/slice/system";
import { RecordAPI } from "../../../../axios/api";
import moment from "moment";

import {
  FormCalendar,
  FormInputnumber,
  FormRadio,
  FormDropdown,
} from "../../../../components/formElement";
import { formatResData, checkFormUpdate } from "../../../../service/common";
import { checkValValid, rateHandler } from "../common";
import { option_warn_limit, errorRecordMsg } from "../../../../service/option";

import { classNames } from "primereact/utils";
import { Button } from "primereact/button";

export default function ModulePopRecordSugarForm({
  prePopOption,
  closePrePop,
  openPrePop,
  setPreReload,
  memberid,
  moduleType,
  memberData,
}) {
  const dispatch = useDispatch();
  const [apiData, setApiData] = useState({});
  // 表單欄位預設值
  const defaultValues = {
    date: moment().format("YYYY/MM/DD"),
    status: null,
    type: null,
    glucose: null,
    memberid: null,
  };
  const {
    handleSubmit,
    getValues,
    setValue,
    reset,
    trigger,
    watch,
    yup,
    setSchema,
    errors,
    setting,
  } = useHookForm({
    defaultValues,
  });
  // 監聽欄位
  const watchColumn = {
    status: watch("status"),
  };

  useEffect(() => {
    setSchema(
      yup
        .object({
          memberid: yup
            .number()
            .test("isValidPass", "必填", (value) => {
              return moduleType === "back" && !memberid ? value : true;
            })
            .nullable(),
          status: yup.number().required("必填"),
          type: yup
            .number()
            .test(
              "isValidPass",
              "必填",
              (value, { from: [{ value: data }] }) => {
                return (data.status === 1 && value) || data.status !== 1;
              }
            )
            .nullable(),
          glucose: yup
            .number()
            .test(
              "isValidPass",
              "必填",
              (value, { from: [{ value: data }] }) => {
                return (data.status === 1 && value) || data.status !== 1;
              }
            )
            .nullable(),
        })
        .required()
    );
  }, []);

  // 動態判斷欄位
  useEffect(() => {
    let submitData = getValues();

    if (submitData.status !== 1) {
      setValue("type", null);
      setValue("glucose", null);
      trigger(["type", "glucose"]);
    }
  }, [watchColumn.status]);

  useAbortedEffect(
    (signal) => {
      if (!prePopOption.data) return;

      dispatch(setProgress(true));

      let params = { id: prePopOption.data.id };

      RecordAPI.getRecordGlucoseDetails({
        data: params,
        options: { signal },
      }).then((result) => {
        let { Response, data, message } = result;

        if (Response === 1) {
          let temp = formatResData(defaultValues, data);
          setApiData(temp);
          reset(temp);
        } else {
          dispatch(
            setToast({
              severity: "error",
              summary: message,
              detail: "",
            })
          );
        }

        dispatch(setProgress(false));
      });
    },
    [prePopOption.data]
  );

  const onSubmit = (submitData) => {
    dispatch(setProgress(true));

    let isAdd = !prePopOption.data,
      jsonData = formatResData(defaultValues, submitData);
    if (jsonData.date)
      jsonData.date = moment(jsonData.date).format("YYYY-MM-DD");

    if (!checkFormUpdate(jsonData, prePopOption.data ?? {})) {
      dispatch(
        setToast({
          severity: "warn",
          summary: "無資料變更",
          detail: "",
        })
      );
      dispatch(setProgress(false));
      closePrePop({ type: "form" });
      return;
    }

    let promise,
      toast = "";
    if (isAdd) {
      // 新增
      promise = RecordAPI.setRecordGlucoseData({
        data: {
          ...jsonData,
          memberid: submitData.memberid ?? memberid,
        },
      });
      toast = "新增";
    } else {
      // 修改
      if (jsonData.date === prePopOption.data.date) delete jsonData.date;
      promise = RecordAPI.updateRecordGlucoseData({
        data: {
          ...jsonData,
          id: prePopOption.data.id,
        },
      });
      toast = "修改";
    }

    promise.then(async (result) => {
      let { Response, my_old_day_data, my_todays_data, message } = result;

      if (Response === 1) {
        await rateHandler({
          memberid: submitData.memberid ?? memberid,
          my_old_day_data,
          my_todays_data,
          old_date: isAdd ? null : prePopOption.data.date,
          now_date: submitData.date,
        });

        dispatch(
          setToast({
            severity: "success",
            summary: `${toast}成功`,
            detail: "",
          })
        );
        closePrePop({ type: "form" });
        setPreReload((state) => ({ ...state, list: true }));

        // 檢查資料異常值，跳出警示通知
        if (
          jsonData.glucose &&
          jsonData.glucose !== prePopOption.data?.glucose &&
          checkValValid({
            warn_limit: option_warn_limit,
            field: "glucose",
            val: jsonData.glucose,
          })
        ) {
          openPrePop({ type: "confirm", data: { msg: errorRecordMsg } });
        }
      } else {
        dispatch(
          setToast({
            severity: "error",
            summary: `${toast}失敗`,
            detail: message ?? "",
          })
        );
        dispatch(setProgress(false));
      }
    });
  };

  return (
    <>
      <div className="px-4">
        <form onSubmit={handleSubmit(onSubmit)} className="formgrid grid">
          {moduleType === "back" && !memberid && (
            <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
              <label
                htmlFor="memberid"
                className={classNames(
                  { "p-error": errors.memberid },
                  "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
                )}
              >
                <span className="text-red-400 mr-1">*</span>
                會員
              </label>
              <div className="col-12 sm:col-9">
                <FormDropdown
                  setting={setting}
                  data={{
                    name: "memberid",
                    options: memberData,
                    optionLabel: "name",
                    optionValue: "id",
                    filter: true,
                    filterBy: "name",
                    disabled: prePopOption.data,
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
            <label
              htmlFor="date"
              className={classNames(
                { "p-error": errors.date },
                "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
              )}
            >
              日期
            </label>
            <div className="col-12 sm:col-9">
              <FormCalendar
                setting={setting}
                data={{
                  name: "date",
                  // disabled: true,
                }}
              />
            </div>
          </div>

          <div className="flex flex-column align-items-start sm:flex-row field col-12">
            <label
              htmlFor="status"
              className={classNames(
                { "p-error": errors.status },
                "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0 sm:mt-2"
              )}
            >
              今日是否量測血糖
            </label>
            <div className="flex flex-wrap col-12 sm:col-9">
              <FormRadio
                setting={setting}
                data={{
                  name: "status",
                  label: "是",
                  value: 1,
                }}
              />
              <FormRadio
                setting={setting}
                data={{
                  name: "status",
                  label: "否",
                  value: 2,
                }}
              />
            </div>
          </div>

          {getValues("status") === 1 && (
            <>
              <div className="flex flex-column align-items-start sm:flex-row field col-12">
                <label
                  htmlFor="type"
                  className={classNames(
                    { "p-error": errors.type },
                    "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0 sm:mt-2"
                  )}
                >
                  量測時間
                </label>
                <div className="flex flex-wrap col-12 sm:col-9">
                  <FormRadio
                    setting={setting}
                    data={{
                      name: "type",
                      label: "飯前",
                      value: 1,
                    }}
                  />
                  <FormRadio
                    setting={setting}
                    data={{
                      name: "type",
                      label: "飯後",
                      value: 2,
                    }}
                  />
                  <FormRadio
                    setting={setting}
                    data={{
                      name: "type",
                      label: "睡前",
                      value: 3,
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-column align-items-start sm:flex-row sm:align-items-center field col-12">
                <label
                  htmlFor="glucose"
                  className={classNames(
                    { "p-error": errors.glucose },
                    "col-12 sm:col-3 text-left sm:text-right mb-1 sm:mb-0"
                  )}
                >
                  血糖值
                </label>
                <div className="flex col-12 sm:col-9">
                  <FormInputnumber
                    className="w-full -mr-1px"
                    setting={setting}
                    data={{
                      name: "glucose",
                    }}
                  />
                  <div className="flex align-items-center justify-content-center w-6rem bg-gray-200 border-1 border-400">
                    mg/dl
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="col-12 text-right">
            <Button
              type="button"
              label="取消"
              className="p-button-secondary p-button-outlined ml-auto px-3"
              onClick={() => closePrePop({ type: "form" })}
            />
            <Button type="submit" label="儲存" className="ml-2 mr-2 px-3" />
          </div>
        </form>
      </div>
    </>
  );
}
